import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  exchangeCodeForTokens,
  refreshAccessToken,
  decodeTokenClaims,
  isTokenExpired,
  buildLogoutUrl,
} from '@/services/auth.service';

const ACCESS_TOKEN_KEY = 'church_admin_access_token';
const REFRESH_TOKEN_KEY = 'church_admin_refresh_token';
const ID_TOKEN_KEY = 'church_admin_id_token';
const SESSION_STARTED_AT_KEY = 'church_admin_session_started_at';

export interface AuthUser {
  sub: string;
  email: string;
  givenName?: string;
  familyName?: string;
  groups?: string[];
}

function readSessionStartedAt(): number | null {
  const stored = sessionStorage.getItem(SESSION_STARTED_AT_KEY);
  if (!stored) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? parsed : null;
}

export const useAuthStore = defineStore('auth', () => {
  // Tokens are stored in sessionStorage (cleared on tab close)
  const accessToken = ref<string | null>(sessionStorage.getItem(ACCESS_TOKEN_KEY));
  const refreshToken = ref<string | null>(sessionStorage.getItem(REFRESH_TOKEN_KEY));
  const idToken = ref<string | null>(sessionStorage.getItem(ID_TOKEN_KEY));
  const user = ref<AuthUser | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /** Epoch milliseconds at which the current ID token expires. The session timer
   *  counts down against this rather than against a duration it accumulates
   *  itself, because a throttled tab's interval drifts badly. */
  const expiresAt = ref<number | null>(null);

  /** Epoch milliseconds of the original sign-in. The refresh token's lifetime runs
   *  from here, so this is what bounds how long a session can be extended. */
  const sessionStartedAt = ref<number | null>(readSessionStartedAt());

  /** A refresh already on the wire. Parallel 401s, a countdown tick and the user's
   *  own button must share one call rather than asking Cognito the same question
   *  three times over. */
  let refreshInFlight: Promise<boolean> | null = null;

  function applySession(claims: ReturnType<typeof decodeTokenClaims>): void {
    user.value = {
      sub: claims.sub,
      email: claims.email,
      givenName: claims.given_name,
      familyName: claims.family_name,
      groups: claims['cognito:groups'],
    };
    expiresAt.value = claims.exp * 1000;
  }

  // Hydrate user from stored id token
  if (idToken.value) {
    try {
      const claims = decodeTokenClaims(idToken.value);
      if (!isTokenExpired(claims)) {
        applySession(claims);
      }
    } catch {
      // Invalid stored token — clear it
      sessionStorage.removeItem(ID_TOKEN_KEY);
      idToken.value = null;
    }
  }

  const isAuthenticated = computed(
    () => accessToken.value !== null && user.value !== null,
  );

  const isAdmin = computed(() => {
    return user.value?.groups?.includes('admin') || false;
  });

  const groups = computed<string[]>(() => user.value?.groups ?? []);

  const isBarrierOperator = computed(
    () => groups.value.includes('barrier-operator'),
  );

  const isBarrierController = computed(
    () => groups.value.includes('barrier-controller'),
  );

  const canAccessCarpark = computed(
    () => isAdmin.value || isBarrierOperator.value || isBarrierController.value,
  );

  const canManageBarrier = computed(
    () => isAdmin.value || isBarrierController.value,
  );

  const canAccessCookbook = computed(
    () => isAdmin.value || groups.value.includes('cookbook'),
  );

  async function handleCallback(code: string, state: string): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      const tokens = await exchangeCodeForTokens(code, state);
      const claims = decodeTokenClaims(tokens.id_token);

      accessToken.value = tokens.access_token;
      refreshToken.value = tokens.refresh_token;
      idToken.value = tokens.id_token;

      const startedAt = Date.now();
      sessionStartedAt.value = startedAt;

      sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
      sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
      sessionStorage.setItem(SESSION_STARTED_AT_KEY, String(startedAt));

      applySession(claims);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Authentication failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function performRefresh(currentRefreshToken: string): Promise<boolean> {
    try {
      const tokens = await refreshAccessToken(currentRefreshToken);
      const claims = decodeTokenClaims(tokens.id_token);

      accessToken.value = tokens.access_token;
      idToken.value = tokens.id_token;

      sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
      sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);

      // sessionStartedAt is deliberately left alone: the refresh token's own
      // lifetime still runs from the original sign-in, so renewing must not
      // extend the ceiling on how long this session can last.
      applySession(claims);
      return true;
    } catch {
      return false;
    }
  }

  /** Exchanges the refresh token for a fresh ID and access token.
   *
   *  `signOutOnFailure` is on by default so existing callers keep the behaviour
   *  they expect. The session timer passes `false`: a refresh that fails on a
   *  flaky connection should leave the warning on screen for the user to retry,
   *  not throw them out of a half-finished edit. */
  async function tryRefreshToken(
    options: { signOutOnFailure?: boolean } = {},
  ): Promise<boolean> {
    const { signOutOnFailure = true } = options;
    if (!refreshToken.value) return false;

    refreshInFlight ??= performRefresh(refreshToken.value).finally(() => {
      refreshInFlight = null;
    });

    const renewed = await refreshInFlight;
    if (!renewed && signOutOnFailure) signOut();
    return renewed;
  }

  function signOut(): void {
    accessToken.value = null;
    refreshToken.value = null;
    idToken.value = null;
    user.value = null;
    expiresAt.value = null;
    sessionStartedAt.value = null;
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ID_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_STARTED_AT_KEY);
    window.location.href = buildLogoutUrl();
  }

  return {
    accessToken,
    idToken,
    user,
    isAuthenticated,
    isLoading,
    error,
    expiresAt,
    sessionStartedAt,
    isAdmin,
    groups,
    isBarrierOperator,
    isBarrierController,
    canAccessCarpark,
    canManageBarrier,
    canAccessCookbook,
    handleCallback,
    tryRefreshToken,
    signOut,
  };
});
